import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {HttpStatusCode} from "@angular/common/http";
import {CustomMessageService, ResponseHttpError, ResponseHttpSuccess, UtilService} from 'library';
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {OriginMessageError} from "library";
import {SalesWorkflow} from "projects/store/src/app/enums";

/**
 * Componente principal de Request Credit Bureau
 * Contiene toda la lógica común para V2 y V3
 * Actúa como container que renderiza la versión correcta según el flujo
 */
@Component({
  selector: 'app-sales-request-credit-bureau',
  templateUrl: './sales-request-credit-bureau.component.html',
})
export class SalesRequestCreditBureauComponent {

  consentForm: FormGroup;
  requestId: string;
  flowCode: string;

  // Estados del documento
  authorizationHTML: string = '';
  documentVersion: string = '';

  // Control de la solicitud
  isSubmitting: boolean = false;

  // Control del modal
  showModal: boolean = false;
  buttonIcon: string = 'pi pi-refresh';
  modalType: 'success' | 'error' = 'success';
  modalTitle: string = '';
  modalMessage: string = '';
  modalButtonLabel: string = 'Continuar';
  lastFailedOperation: 'loadDocument' | 'registerConsent' | null = null;

  @Output() nextStepEvent = new EventEmitter<void>();

  constructor(
    protected requestService: RequestService,
    protected route: ActivatedRoute,
    protected customMessageService: CustomMessageService,
    protected utilService: UtilService
  ) {
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.consentForm = new FormGroup({
      acceptTerms: new FormControl(false, [Validators.requiredTrue])
    });
  }

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-cliente'] || this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  ngOnInit() {
    // Container: la inicialización se delega a los hijos (V2/V3)
  }

  loadDocument() {
    this.requestService.getCreditBureau(this.requestId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.authorizationHTML = resp.body.data.authorization_text;
        }
        this.utilService.disablePageLoading();
      },
      error: (error: ResponseHttpError) => {
        console.error('Error al cargar el documento:', error);
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
        this.utilService.disablePageLoading();
      }
    });
  }

  onConfirm() {
    if (this.consentForm.valid) {
      this.registerConsent();
    }
  }

  async registerConsent() {
    // RB-04: Guard de idempotencia - prevenir múltiples solicitudes
    if (this.isSubmitting || !this.authorizationHTML) {
      return;
    }

    this.isSubmitting = true;
    this.consentForm.disable();

    // Obtener geolocalización solo si ya tiene permiso concedido
    const geolocation = await this.getGeolocation();

    const consentData = {
      document_html: this.authorizationHTML,
      accepted: true,
      metadata: {
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        screen: {
          width: screen.width,
          height: screen.height,
          pixel_ratio: window.devicePixelRatio
        },
        connection: this.getConnectionInfo(),
        geolocation: geolocation
      }
    };

    this.requestService.registerCreditBureauConsent(this.requestId, consentData).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Created) {
          this.utilService.enablePageLoading();
          this.onNextStep();
        } else {
          this.customMessageService.responseNoticeHandler(resp, OriginMessageError.BACKEND);
          this.isSubmitting = false;
          this.consentForm.enable();
        }
      },
      error: (error: ResponseHttpError) => {
        console.error('Error al registrar consentimiento:', error);
        this.utilService.disablePageLoading();
        this.isSubmitting = false;
        this.consentForm.enable();
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  protected async getGeolocation(): Promise<{latitude: number, longitude: number} | null> {
    try {
      // Verificar si el permiso ya está concedido sin solicitarlo
      const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });

      if (permission.state !== 'granted') {
        return null; // No solicitar permiso, solo retornar null
      }

      // Si ya tiene permiso, obtener la ubicación
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
          () => resolve(null), // En caso de error, retornar null
          { timeout: 3000, maximumAge: 60000 } // Usar caché si está disponible
        );
      });
    } catch {
      return null; // Si la API no está disponible, retornar null
    }
  }

  protected getConnectionInfo(): any {
    const conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (!conn) return null;

    return {
      effective_type: conn.effectiveType || null,
      downlink: conn.downlink || null,
      rtt: conn.rtt || null
    };
  }

  onNextStep(): void {
    this.nextStepEvent.emit();
  }
}